/**
 * Обёртка поля: подпись, подсказка, ошибка.
 *
 * Связывает всё через id и aria-describedby. Ошибка, показанная только цветом
 * рамки, недоступна: §19.5 требует текстовой подписи, а цвет — не подпись.
 */

import { useId, type ReactNode } from "react";

import { cn } from "./cn";

interface FormFieldProps {
  label: string;
  /** Функция получает готовые id и aria-атрибуты — их нельзя забыть проставить. */
  children: (props: {
    id: string;
    "aria-describedby": string | undefined;
    "aria-invalid": boolean | undefined;
  }) => ReactNode;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
}

export function FormField({
  label,
  children,
  hint,
  error,
  required = false,
  className,
}: FormFieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(" ") || undefined;

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <label
        htmlFor={id}
        className="text-[13px] font-semibold text-muted"
        // Щелчок по подписи браузер передаёт связанному полю. Текстовому
        // полю это даёт фокус, а кнопке выпадающего списка — нажатие:
        // список раскрывался от щелчка мимо него. Такие поля открываются
        // только сами.
        onClick={(event) => {
          const control = document.getElementById(id);
          if (control?.tagName === "BUTTON" || control?.getAttribute("role") === "combobox") {
            event.preventDefault();
          }
        }}
      >
        {label}
        {required && (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        )}
      </label>

      {children({
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
      })}

      {error ? (
        <p id={errorId} className="text-xs font-semibold text-danger" role="alert">
          {error}
        </p>
      ) : (
        hint && (
          <p id={hintId} className="text-xs text-faint">
            {hint}
          </p>
        )
      )}
    </div>
  );
}
